import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Layout = ({ children }) => {
  const { user, tenant, logout, lcoFeatures } = useAuth();
  const isPilotLco = lcoFeatures?.collections || false;
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!user || !tenant) return children;

  const primary = tenant.branding.primary_color || '#1B6B3A';
  const logoUrl = tenant.branding.logo_url
    ? (tenant.branding.logo_url.startsWith('http') ? tenant.branding.logo_url : window.location.origin + tenant.branding.logo_url)
    : null;
  const appName = tenant.branding.app_name || 'FibraDesk';

  const isActive = (path) => location.pathname === path;
  const isSuperAdmin = user.role === 'superadmin';
  const isISPAdmin = user.role === 'isp_admin';
  const isDistHead = user.role === 'dist_head';
  const isSubDist = ['subdist_tech_own','subdist_tech_isp','subdist_admin'].includes(user.role);
  const isManager = isSuperAdmin || isISPAdmin || isDistHead || user.role === 'tenant_admin';
  const isDirector = user.role === 'director';
  const isMonitor = ['network_monitor','dist_l1','dist_l2','dist_l3'].includes(user.role);
  const isCrmStaff = user.role === 'crm_staff';
  const isLco = user.role === 'lco';
  const isIspDistHead = user.role === 'isp_dist_head';
  const isAreaEngineer = user.role === 'area_engineer';
  const canCreateTicket = !isDirector && !isIspDistHead && !isAreaEngineer && user.role !== 'subdist_admin' &&
    (isSuperAdmin || isISPAdmin || ['subdist_tech_own','subdist_tech_isp'].includes(user.role) || ['isp_technician','dist_head','dist_l1','dist_l2','dist_l3'].includes(user.role) || (isCrmStaff && user.can_create_ticket));

  const pageTitle = () => {
    const titles = {
      '/': 'Dashboard', '/tickets': 'All Tickets', '/tickets/new': 'New Ticket',
      '/reports': 'Reports', '/kra': 'KRA Report', '/monthly-report': '📊 Monthly Report', '/network': 'Network', '/users': 'User Management',
      '/settings': 'Settings', '/tenants': 'ISP / Distributors',
      '/profile': 'My Profile', '/tech-reports': 'Tech Work Reports',
      '/monitor-reports': 'Monitor Reports', '/crm': appName + ' CRM', '/support-calls': 'Support Calls', '/weekly-reports': 'Weekly Network Reports'
    };
    if (location.pathname.startsWith('/tickets/')) return 'Ticket Detail';
    return titles[location.pathname] || appName;
  };

  const MenuItem = ({ icon, label, path, badge }) => {
    const active = isActive(path);
    return (
      <div onClick={() => { navigate(path); if(isMobile) setMobileOpen(false); }} style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: collapsed ? '12px' : '10px 14px',
        borderRadius: '10px', cursor: 'pointer', marginBottom: '2px',
        background: active ? `${primary}22` : 'transparent',
        color: active ? primary : '#64748b',
        fontWeight: active ? '600' : '400', fontSize: '14px',
        transition: 'all 0.15s', position: 'relative',
        borderLeft: active ? `3px solid ${primary}` : '3px solid transparent',
        justifyContent: collapsed ? 'center' : 'flex-start'
      }}
        onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#1e293b'; }}}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}}>
        <span style={{ fontSize: '17px', flexShrink: 0 }}>{icon}</span>
        {!collapsed && <span style={{ flex: 1 }}>{label}</span>}
        {!collapsed && badge > 0 && (
          <span style={{ background: '#ef4444', color: 'white', borderRadius: '20px', padding: '1px 7px', fontSize: '11px', fontWeight: '700' }}>{badge}</span>
        )}
        {collapsed && badge > 0 && (
          <span style={{ position: 'absolute', top: '6px', right: '6px', background: '#ef4444', color: 'white', borderRadius: '50%', width: '14px', height: '14px', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>{badge}</span>
        )}
      </div>
    );
  };

  const SectionLabel = ({ label }) => !collapsed && (
    <p style={{ margin: '20px 0 6px 14px', fontSize: '10px', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px' }}>{label}</p>
  );

  const Divider = () => <div style={{ height: '1px', background: '#f1f5f9', margin: '8px 0' }} />;

  return (
    <div id="app-layout" style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Mobile overlay */}
      {mobileOpen && <div onClick={()=>setMobileOpen(false)} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:999, display:'none' }} className='mobile-overlay' />}
      {/* Sidebar */}
      <div id="app-sidebar" style={{
        width: collapsed ? '68px' : '248px',
        background: 'white',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.25s ease, transform 0.25s ease',
        position: 'fixed', top: 0, left: 0,
        height: '100vh', zIndex: 1000,
        boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
        transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'translateX(0)'
      }}>

        {/* Logo area */}
        <div style={{
          padding: collapsed ? '12px 8px' : '12px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: collapsed ? '60px' : '100px'
        }}>
          {!collapsed && (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', cursor:'pointer' }} onClick={() => navigate('/')}>
              {logoUrl ? (
                <img src="/uploads/photos/combined_white.png?v=3" alt="PDIC KV Nexus"
                  style={{ width:'100%', maxWidth:'210px', objectFit:'contain', display:'block' }} />
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <div style={{ width:'36px', height:'36px', borderRadius:'10px', background:`linear-gradient(135deg, ${primary}, ${primary}88)`, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:'700', fontSize:'16px' }}>
                    {appName.charAt(0)}
                  </div>
                  <div style={{ color:'#1e293b', fontWeight:'700', fontSize:'14px' }}>{appName}</div>
                </div>
              )}
            </div>
          )}
          {collapsed && (
            <div style={{ width:'36px', height:'36px', borderRadius:'10px', background:`linear-gradient(135deg, ${primary}, ${primary}88)`, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:'700', fontSize:'16px', cursor:'pointer' }} onClick={() => navigate('/')}>
              {appName.charAt(0)}
            </div>
          )}
          {!collapsed && (
            <button onClick={() => setCollapsed(true)} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'6px', width:'28px', height:'28px', cursor:'pointer', color:'#64748b', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>◀</button>
          )}
        </div>

        {/* Collapse button when collapsed */}
        {collapsed && (
          <button onClick={() => setCollapsed(false)} style={{ margin: '8px auto', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', width: '36px', height: '28px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▶</button>
        )}

        {/* Navigation */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px', scrollbarWidth: 'none' }}>
          <SectionLabel label="Main" />
          {isLco && <MenuItem icon="🏠" label="Dashboard" path="/lco" />}
          {isLco && <MenuItem icon="➕" label="New Ticket" path="/lco?tab=create" />}
          {isLco && <MenuItem icon="📡" label="My Network" path="/lco?tab=network" />}
          {isLco && isPilotLco && <MenuItem icon="💰" label="Collections" path="/lco?tab=payments" />}
          {isLco && lcoFeatures?.expenses && <MenuItem icon="📒" label="Daily Expenses" path="/lco/expenses" />}
          {isLco && <MenuItem icon="💳" label="Payment Setup" path="/lco/payment-setup" />}
          {isCrmStaff && <MenuItem icon="📋" label="CRM Log" path="/crm" />}
          {isCrmStaff && user.can_log_support && <MenuItem icon="📞" label="Support Calls" path="/support-calls" />}
          {!isLco && <MenuItem icon="🏠" label="Dashboard" path="/" />}
          {!isCrmStaff && !isLco && <MenuItem icon="🎫" label="All Tickets" path="/tickets" />}
          {!isCrmStaff && !isLco && canCreateTicket && <MenuItem icon="➕" label="New Ticket" path="/tickets/new" />}

          {!isLco && (isManager || isDirector || isIspDistHead || isMonitor) && (
            <>
              <Divider />
              <SectionLabel label="Management" />
              {(isISPAdmin || isDistHead || isDirector) && <MenuItem icon="🧠" label="Analytics" path="/analytics" />}
              {(isISPAdmin || isDistHead || isDirector) && <MenuItem icon="🎯" label="Target Tracker" path="/target-tracker" />}
              {(isISPAdmin || isDistHead || isDirector) && <MenuItem icon="👥" label="Customer Analytics" path="/customer-analytics" />}
              {user.role !== 'dist_l1' && <MenuItem icon="📋" label="Reports" path="/reports" />}
              {(isManager||isDirector) && <MenuItem icon="🎫" label="Ticket Analysis" path="/ticket-report" />}
              {(isManager||isDirector) && <MenuItem icon="🔍" label="Ticket Search" path="/ticket-search" />}
              {(isManager||isDirector) && <MenuItem icon="📋" label="LCO Feedback" path="/lco-feedback-report" />}
              {user.role !== 'dist_l1' && <MenuItem icon="📊" label="KRA Report" path="/kra" />}
              {user.role !== 'dist_l1' && <MenuItem icon="📋" label="Monthly Report" path="/monthly-report" />}
              {user.role !== 'dist_l1' && <MenuItem icon="🧑‍🔧" label="Tech Reports" path="/tech-reports" />}
              <MenuItem icon="📡" label="Monitor Reports" path="/monitor-reports" />
              {!isCrmStaff && user.role !== 'dist_l1' && <MenuItem icon="📝" label="CRM Log" path="/crm" />}
              {(user.role==='dist_admin'||user.role==='dist_head'||user.role==='director'||user.role==='tenant_admin') && <MenuItem icon="📞" label="Support Calls" path="/support-calls" />}
              {(user.role==='dist_admin'||user.role==='tenant_admin') && <MenuItem icon="⚙️" label="LCO Features" path="/lco-features" />}
            </>
          )}

          {/* Weekly Reports - for sub-dist techs and admins */}
          {(user.role==='dist_admin'||user.role==='tenant_admin'||user.role==='dist_head') && (
            <>
              <Divider />
              <SectionLabel label="LCO Management" />
              <MenuItem icon="*" label="LCO Features" path="/lco-features" />
            </>
          )}

          {(isSubDist || isManager || isDirector || isIspDistHead || ['dist_admin','dist_head','dist_l1'].includes(user.role)) && (
            <>
              <Divider />
              <SectionLabel label="Weekly Reports" />
              <MenuItem icon="📅" label="Weekly Reports" path="/weekly-reports" />
            </>
          )}
          {!isCrmStaff && (isSuperAdmin || isISPAdmin || isDistHead || user.role === 'subdist_admin') && (
            <>
              <Divider />
              <SectionLabel label="Network" />
              {(isSuperAdmin || isISPAdmin) && <MenuItem icon="🌐" label="Network" path="/network" />}
              {isDistHead && <MenuItem icon="🌐" label="Sub Distributions" path="/network" />}
              {user.role === 'subdist_admin' && <MenuItem icon="🏘️" label="LCOs" path="/network" />}
              {(isManager || user.role === 'subdist_admin') && <MenuItem icon="👥" label="Users" path="/users" />}
              {user.role === 'subdist_admin' && <MenuItem icon="👥" label="Customer Analytics" path="/customer-analytics" />}
              {user.role === 'subdist_admin' && <MenuItem icon="🎯" label="Target Tracker" path="/target-tracker" />}
            </>
          )}

          {isSuperAdmin && (
            <>
              <Divider />
              <SectionLabel label="Platform" />
              <MenuItem icon="🏢" label="ISP / Distributors" path="/tenants" />
              <MenuItem icon="⚙️" label="Settings" path="/settings" />
            </>
          )}

          {(isISPAdmin || isDistHead) && (
            <>
              <Divider />
              <SectionLabel label="Settings" />
              <MenuItem icon="⚙️" label="Settings" path="/settings" />
            </>
          )}

          <Divider />
          <SectionLabel label="Account" />
          <MenuItem icon="👤" label="My Profile" path="/profile" />

          {/* Public complaint URL */}
          {!collapsed && tenant && (
            <div style={{ margin: '16px 4px 8px', padding: '12px', background: `${primary}10`, borderRadius: '10px', border: `1px solid ${primary}20` }}>
              <p style={{ margin: '0 0 4px', fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>Public Complaint URL</p>
              <p style={{ margin: '0 0 8px', fontSize: '11px', color: primary, fontWeight: '600', wordBreak: 'break-all' }}>
                complaint.pdic.support
              </p>
              <button onClick={() => window.open('https://complaint.pdic.support', '_blank')}
                style={{ width: '100%', padding: '6px', background: primary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                🔗 Open Public Page
              </button>
            </div>
          )}
        </div>

        {/* User area */}
        <div style={{ padding: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: '10px', padding: '10px 12px',
            borderRadius: '10px', background: '#f8fafc',
            marginBottom: '4px',
            justifyContent: collapsed ? 'center' : 'flex-start'
          }}>
            <div style={{
              width: '34px', height: '34px', borderRadius: '50%',
              background: `linear-gradient(135deg, ${primary}, ${primary}aa)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: '700', fontSize: '14px', flexShrink: 0
            }}>{user.name.charAt(0).toUpperCase()}</div>
            {!collapsed && (
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '13px', color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</p>
                <p style={{ margin: 0, fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textTransform: 'capitalize' }}>{user.role.replace(/_/g, ' ')}</p>
              </div>
            )}
          </div>
          <div onClick={logout} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: collapsed ? '10px' : '10px 12px',
            borderRadius: '10px', cursor: 'pointer',
            color: '#ef4444', fontSize: '13px', fontWeight: '500',
            transition: 'all 0.15s',
            justifyContent: collapsed ? 'center' : 'flex-start'
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span style={{ fontSize: '16px' }}>🚪</span>
            {!collapsed && <span>Logout</span>}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ marginLeft: isMobile ? '0' : (collapsed ? '68px' : '248px'), flex: 1, transition: 'margin-left 0.25s ease', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

        {/* Top bar */}
        <div id="app-topbar" style={{
          background: 'white',
          padding: '0 24px',
          height: '60px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 999,
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={()=>setMobileOpen(!mobileOpen)} style={{ display:'none', background:'none', border:'none', cursor:'pointer', fontSize:'22px', padding:'4px', color:'#1e293b' }} className='hamburger-btn'>☰</button>
            <div style={{ width: '4px', height: '20px', background: primary, borderRadius: '2px' }} />
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{pageTitle()}</span>
            <span style={{ fontSize: '13px', color: '#94a3b8', marginLeft: '4px' }}>/ {appName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
            </div>
            <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `linear-gradient(135deg, ${primary}, ${primary}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '13px' }}>
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{user.name.split(' ')[0]}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'capitalize' }}>{user.role.replace(/_/g, ' ')}</div>
              </div>
            </div>
            {canCreateTicket && (
              <button onClick={() => navigate('/tickets/new')} style={{
                background: primary, color: 'white', border: 'none',
                padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600',
                boxShadow: `0 4px 12px ${primary}40`,
                display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                ➕ New Ticket
              </button>
            )}
          </div>
        </div>

        {/* Page content */}
        <div style={{ padding: '24px', flex: 1, background: '#f1f5f9' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
